import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../services/supabase';

export type TableStatus = 'free' | 'occupied' | 'payment_received' | 'needs_attention' | 'reserved';

export interface StaffTable {
  id: string;
  table_number: number;
  capacity: number;
  section: string | null;
  floor: number;
  status: TableStatus;
  current_order_id: string | null;
  current_amount: number | null;
  payment_channel: 'nfc' | 'qr' | 'cash' | 'zazu' | 'card' | null;
  assigned_staff_id: string | null;
  last_activity_at: string | null;
  nfc_uid: string | null;
  nfc_active: boolean;
}

function mapPaymentMethod(method: string | null): StaffTable['payment_channel'] {
  if (method === 'tapnpay_nfc') return 'nfc';
  if (method === 'tapnpay_qr') return 'qr';
  if (method === 'zazu') return 'zazu';
  if (method === 'card' || method === 'apple_pay' || method === 'google_pay') return 'card';
  if (method === 'cash') return 'cash';
  return null;
}

function mapRow(row: Record<string, unknown>): StaffTable {
  const orders = (row.orders as Record<string, unknown>[] | null) ?? [];
  const activeOrder = orders[0] ?? null;
  const info = activeOrder?.customer_info as Record<string, unknown> | null;

  let status: TableStatus = 'free';
  if (row.status === 'reserved') status = 'reserved';
  else if (activeOrder) {
    const payStatus = info?.payment_status as string | null;
    if (payStatus === 'paid') status = 'payment_received';
    else if (payStatus === 'pending_cash') status = 'needs_attention';
    else status = 'occupied';
  }

  return {
    id: row.id as string,
    table_number: row.table_number as number,
    capacity: row.capacity as number,
    section: row.section as string | null,
    floor: (row.floor as number) ?? 1,
    status,
    current_order_id: (activeOrder?.id as string | null) ?? null,
    current_amount: (activeOrder?.total_amount as number | null) ?? null,
    payment_channel: mapPaymentMethod(activeOrder?.payment_method as string | null),
    assigned_staff_id: (activeOrder?.employee_id as string | null) ?? null,
    last_activity_at: (activeOrder?.created_at as string | null) ?? null,
    nfc_uid: row.nfc_uid as string | null,
    nfc_active: row.nfc_active as boolean,
  };
}

export function useTables(storeId: string) {
  const [tables, setTables] = useState<StaffTable[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTables = useCallback(async () => {
    const { data } = await supabase
      .from('restaurant_tables')
      .select(`
        id, table_number, capacity, section, floor, status, nfc_uid, nfc_active,
        orders!orders_table_id_fkey (
          id, total_amount, payment_method, created_at, employee_id, customer_info
        )
      `)
      .eq('store_id', storeId)
      .order('table_number');

    if (data) {
      setTables((data as Record<string, unknown>[]).map(mapRow));
    }
    setLoading(false);
  }, [storeId]);

  useEffect(() => {
    fetchTables();

    const channel = supabase
      .channel(`staff-tables-${storeId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `store_id=eq.${storeId}` }, () => { void fetchTables(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'restaurant_tables', filter: `store_id=eq.${storeId}` }, () => { void fetchTables(); })
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [storeId, fetchTables]);

  return { tables, loading, refetch: fetchTables };
}
